import { useEffect, useRef, useState } from "react";
import { API_BASE } from "@/lib/api";
import { T } from "@/lib/theme";

const C = T.term;
const TOKEN_KEY = "wsp_token";

/** Live delivery ticker driven by Server-Sent Events.
 *  Shows the last 20 auto-issued certificates as they fire from the crawler.
 */
export function DeliveryTicker() {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    const url = `${API_BASE}/feed/live?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;
    es.addEventListener("hello", () => setConnected(true));
    es.addEventListener("airplay", (e) => {
      try {
        const d = JSON.parse(e.data);
        setEvents((prev) => [{ ...d, _id: `${d.certificate_number}-${Date.now()}` }, ...prev].slice(0, 20));
      } catch {}
    });
    es.onerror = () => setConnected(false);
    return () => es.close();
  }, []);

  return (
    <div data-testid="delivery-ticker" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: connected ? C.green : "#EF4444",
            animation: connected ? "pulseGr 1.6s ease-out infinite" : "none" }} />
          LIVE DELIVERY FEED
          <span data-testid="ticker-status" style={{ color: C.muted, fontWeight: 400, fontSize: 11 }}>
            {connected ? "connected" : "reconnecting…"}
          </span>
        </div>
        <span style={{ color: C.muted, fontSize: 11, fontFamily: C.mono }}>SSE · /feed/live</span>
      </div>
      <style>{`@keyframes pulseGr{0%{box-shadow:0 0 0 0 ${C.green}aa}70%{box-shadow:0 0 0 8px ${C.green}00}100%{box-shadow:0 0 0 0 ${C.green}00}}
        @keyframes slideInTk{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}`}</style>
      {events.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 12, fontFamily: C.mono, padding: "16px 0", textAlign: "center" }}>
          Waiting for the next ad detection… The crawler fires every 60 seconds.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto" }}>
          {events.map((e) => (
            <div key={e._id} data-testid={`ticker-row-${e.certificate_number}`}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
                animation: "slideInTk 0.4s ease-out", fontSize: 12 }}>
              <span style={{ background: C.greenDim, color: C.green, fontSize: 9, fontWeight: 800,
                padding: "2px 7px", borderRadius: 4, fontFamily: C.mono, letterSpacing: "0.08em",
                whiteSpace: "nowrap" }}>AIRED</span>
              <span style={{ color: C.text, fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <b>{e.ad_title}</b> on {e.station_name}
              </span>
              <span style={{ color: C.green, fontFamily: C.mono, fontWeight: 700, whiteSpace: "nowrap" }}>
                {(e.reach || 0).toLocaleString()} reach
              </span>
              <span style={{ color: C.muted, fontFamily: C.mono, fontSize: 10, whiteSpace: "nowrap" }}>
                {e.certificate_number?.slice(-9) || ""}
              </span>
              <a href={`/verify/${e.certificate_number}`} target="_blank" rel="noreferrer"
                style={{ color: C.green, fontSize: 11, textDecoration: "none", whiteSpace: "nowrap" }}>verify ↗</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
